import {
  DocumentExternal,
  DocumentId,
  nominate,
  TrustCenterAiCheckItemConfig,
  TrustCenterAiDataUseItemConfig,
  TrustCenterAiModelProviderConfig,
  TrustCenterDataProtectionDataUseItemConfig,
  TrustCenterFaq,
  TrustCenterSubscribeConfig,
} from "platformed-client/types";
import {q, useQueriesData, useQueryData} from "../state";
import externalApi from "../api/external";
import {useClientAccount} from "./hooks/clientAccount";
import {getFirstRootEntity} from "../Products/VendorToolkit/graph/util";
import {useMemo} from "react";
import {getSubprocessorsFromEntityGraph} from "../Products/VendorToolkit/graph/types/subprocessor";
import {getPeopleFromEntityGraph, OrganizationRelationship} from "../Products/VendorToolkit/graph/types/person";
import {getAssetUrl} from "../api/external/trustCenters";
import useFormatDate from "platformed-components/i18n/useFormatDate";

export type DocumentT = {name: string; requiredPermission?: string};

export type SubprocessorT = {
  id: string;
  name: string;
  location?: string;
  gdpr?: ("SCC" | "EUR" | "EQUIVALENCE")[];
  url?: string;
  description?: string;
  trust_center_url?: string;
  logo_src?: string;
  shared_data?: string[];
};

export interface TrustCenterDataProtectionOperatingCountriesT {
  iso_country_code: string;
  detailed_address?: string;
  description?: string;
}

export type PersonT = {name: string; role?: string; src?: string; url?: string};

export type KybEntry =
  | {key: string; type: "ENTITIES"; value: PersonT[]}
  | {key: string; type: "STRING"; value?: string}
  | {key: string; type: "URL"; value?: string[]};

export type KybT = KybEntry[];

export type CertificationT = {image_url: string};

type Json = null | boolean | number | string | readonly Json[] | JsonObject;
type JsonObject = {readonly [key: string]: Json};

export type TrustCenterOverview = {
  welcome?: string;
  content?: string;
  trusted_partnered?: string;
  trusted_by?: {image_url: string; link_url: string}[];
};

export type BusinessContent = {title?: string; content?: string};

/** `TrustCenterAiModelProviderConfig` with the related subprocessor materialized. */
export type AiModelProvider = Omit<TrustCenterAiModelProviderConfig, "subprocessor_entity_id"> & {
  subprocessor: SubprocessorT;
};

export type TrustCenterAi = {
  overview?: string;
  related_documents: DocumentId[];
  check_items: TrustCenterAiCheckItemConfig[];
  data_use_items: TrustCenterAiDataUseItemConfig[];
  model_providers: AiModelProvider[];
  faq: TrustCenterFaq;
};

export type TrustCenterDataProtection = {
  overview?: string;
  related_documents: DocumentId[];
  data_use_items: TrustCenterDataProtectionDataUseItemConfig[];
  operating_countries: TrustCenterDataProtectionOperatingCountriesT[];
  subprocessors: boolean;
  sar: boolean;
};

export type ConfigT = {
  name: string;
  logo_url?: string;
  homepage_url?: string;
  favicon_url?: string;
  subprocessors: SubprocessorT[];
  kyb: KybT;
  chakraTheme?: JsonObject;
  hero: {heading: string; subheading?: string};
  overview: TrustCenterOverview;
  business_info?: BusinessContent[];
  ai?: TrustCenterAi;
  data_protection?: TrustCenterDataProtection;
  faq: TrustCenterFaq;
  is_public: boolean;
  subscribe?: TrustCenterSubscribeConfig;
  privacy_policy?: string;
};

export function useTrustCenterConfig(): ConfigT {
  const account = useClientAccount();
  const formatDate = useFormatDate();
  const config = useQueryData(q.external.trustCenterConfig(account));
  const faq = useQueryData(q.external.trustCenterFaq(account));
  const aiFaq = useQueryData(q.external.trustCenterAiFaq(account));
  const graph = useQueryData(q.external.trustCenterGraph(account, {filter_properties: ["is_root_legal_entity"]}));

  // Build a list of all the content we need to load for this trust center
  const contentIds = [];
  if (config.meta.overview?.welcome_id) {
    contentIds.push(config.meta.overview?.welcome_id);
  }
  if (config.meta.overview?.content_id) {
    contentIds.push(config.meta.overview?.content_id);
  }
  if (config.meta.business_info) {
    for (const c of config.meta.business_info) {
      if (c.content_id) {
        contentIds.push(c.content_id);
      }
    }
  }
  if (config.meta.ai?.overview_content_id) {
    contentIds.push(config.meta.ai?.overview_content_id);
  }
  if (config.meta.data_protection?.overview_content_id) {
    contentIds.push(config.meta.data_protection?.overview_content_id);
  }

  const contents = useQueriesData({
    queries: contentIds.map(contentId => q.external.trustCenterContent(account, contentId)),
  });

  // Build legal and subprocessor information from entity graph

  const legalEntity = useMemo(() => getFirstRootEntity(graph), [graph]);

  const kyb = useMemo((): KybT => {
    if (!legalEntity) {
      return [];
    }
    const graphPeople = getPeopleFromEntityGraph(graph, legalEntity.id);

    const peopleEntries: {[K in OrganizationRelationship]: PersonT[]} = {
      beneficial_owner: [],
      board_member: [],
      senior_leadership: [],
    };
    for (const p of graphPeople) {
      const person: PersonT = {
        name: p.name,
        role: p.jobTitle,
        url: p.websiteUrl.length === 0 ? undefined : p.websiteUrl,
        src: p.photoAssetId ? getAssetUrl(account, p.photoAssetId) : undefined,
      };

      for (const r of p.organizationRelationships) {
        peopleEntries[r].push(person);
      }
    }

    // A hardcoded list of properties on the trust center. In the future, we may
    // want to populate this dynamically using the /schema endpoint to get the
    // list of properties and their labels.
    return (
      [
        {type: "STRING", key: "Name", value: legalEntity.properties["name"]},
        {type: "STRING", key: "Country of registration", value: legalEntity.properties["country_of_registration"]},
        {type: "STRING", key: "Companies House number", value: legalEntity.properties["companies_house_number"]},
        {type: "STRING", key: "D-U-N-S number", value: legalEntity.properties["duns_number"]},
        {type: "STRING", key: "VAT number", value: legalEntity.properties["vat_number"]},
        {type: "STRING", key: "ICO registration", value: legalEntity.properties["ico_registration"]},
        {type: "STRING", key: "Registered address", value: legalEntity.properties["registered_address"]},
        {
          type: "STRING",
          key: "Incorporation date",
          value: legalEntity.properties["incorporation_date"]
            ? formatDate(nominate("date", legalEntity.properties["incorporation_date"]), {dateStyle: "long"}) ||
              undefined
            : undefined,
        },
        {
          type: "STRING",
          key: "Number of employees",
          value: legalEntity.properties["number_of_employees"]
            ? legalEntity.properties["number_of_employees"].toString()
            : undefined,
        },
        {type: "STRING", key: "Industry", value: legalEntity.properties["industry_type"]},
        {type: "STRING", key: "Growth plan/strategic direction", value: legalEntity.properties["growth_plan"]},
        {
          type: "STRING",
          key: "Legal entity type",
          value:
            legalEntity.properties["official_legal_entity_type"] === "Other (please specify)"
              ? ""
              : legalEntity.properties["official_legal_entity_type"],
        },

        {type: "ENTITIES", key: "Beneficial owners", value: peopleEntries.beneficial_owner},
        {type: "ENTITIES", key: "Board members", value: peopleEntries.board_member},
        {type: "ENTITIES", key: "Senior leadership", value: peopleEntries.senior_leadership},
        {
          type: "URL",
          key: "Website",
          value: legalEntity.properties["website_url"] ? [legalEntity.properties["website_url"]] : undefined,
        },
      ] satisfies KybT
    ).filter(r => r.value !== undefined && r.value.length !== 0);
  }, [account, formatDate, graph, legalEntity]);

  const subprocessors = useMemo(
    (): SubprocessorT[] =>
      legalEntity
        ? getSubprocessorsFromEntityGraph(graph, legalEntity.id).map(s => ({
            id: s.id,
            name: s.name,
            description: s.description,
            location: s.isoCountryCode,
            url: s.websiteUrl,
            trust_center_url: s.trustCenterUrl,
            shared_data: s.sharedData.length === 0 ? undefined : s.sharedData.split("\n"),
            gdpr: [
              ...(s.gdprSccs ? (["SCC"] as const) : []),
              ...(s.gdprBasedInEurope ? (["EUR"] as const) : []),
              ...(s.gdprEquivalence ? (["EQUIVALENCE"] as const) : []),
            ],
            logo_src: undefined,
          }))
        : [],
    [graph, legalEntity],
  );

  // Filter only model providers with an associated existing subprocessor.
  const aiModelProviders = useMemo(
    () =>
      config.meta.ai?.model_providers?.flatMap(modelProvider => {
        if (modelProvider.subprocessor_entity_id == null) {
          return [];
        }

        const subprocessor = subprocessors.find(s => s.id === modelProvider.subprocessor_entity_id);

        if (subprocessor == null) {
          return [];
        }

        return {
          subprocessor,
          purposes: modelProvider.purposes,
          models_used: modelProvider.models_used,
        } satisfies AiModelProvider;
      }) ?? [],
    [config.meta.ai?.model_providers, subprocessors],
  );

  return {
    name: config.meta.name ?? "",
    logo_url:
      (config.meta.logo_asset_id && externalApi.trustCenters.getAssetUrl(account, config.meta.logo_asset_id)) ??
      undefined,
    homepage_url: config.meta.homepage_url ?? "",
    favicon_url: config.meta.favicon_asset_id
      ? externalApi.trustCenters.getAssetUrl(account, config.meta.favicon_asset_id)
      : undefined,
    subprocessors,
    kyb,
    chakraTheme: config.theme,
    hero: {heading: config.meta.hero?.heading ?? "", subheading: config.meta.hero?.subheading ?? undefined},
    overview: {
      content: contents.find(content => content.content_id === config.meta.overview?.content_id)?.content ?? "",
      welcome: contents.find(content => content.content_id === config.meta.overview?.welcome_id)?.content ?? "",
      trusted_partnered: config.meta.overview?.trusted_partnered_title,
      trusted_by: config.meta.overview?.trusted_by?.map(trustedBy => ({
        image_url: trustedBy.logo_asset_id
          ? externalApi.trustCenters.getAssetUrl(account, trustedBy.logo_asset_id)
          : "",
        link_url: trustedBy.logo_link_url ?? "",
      })),
    },
    business_info: config.meta.business_info?.map(businessContent => ({
      title: businessContent.title ?? "",
      content: contents.find(content => content.content_id === businessContent.content_id)?.content ?? "",
    })),
    ai:
      config.meta.ai != null
        ? {
            overview:
              contents.find(content => content.content_id === config.meta.ai!.overview_content_id)?.content ?? "",
            related_documents:
              config.meta.ai.related_documents?.flatMap(d => (d.document_id ? [d.document_id] : [])) ?? [],
            check_items: config.meta.ai.check_items ?? [],
            data_use_items: config.meta.ai.data_use_items ?? [],
            model_providers: aiModelProviders,
            faq: aiFaq,
          }
        : undefined,
    data_protection:
      config.meta.data_protection != null && config.meta.data_protection.enabled
        ? {
            overview:
              contents.find(content => content.content_id === config.meta.data_protection!.overview_content_id)
                ?.content ?? "",
            related_documents:
              config.meta.data_protection.related_documents?.flatMap(d => (d.document_id ? [d.document_id] : [])) ?? [],
            data_use_items: config.meta.data_protection.data_use_items ?? [],
            operating_countries:
              config.meta.data_protection.operating_countries?.map(c => ({
                iso_country_code: c.iso_country_code,
                detailed_address: c.detailed_address ?? undefined,
                description: c.description ?? undefined,
              })) ?? [],
            subprocessors: config.meta.data_protection.subprocessors_enabled ?? false,
            sar: config.meta.data_protection.sar_enabled ?? false,
          }
        : undefined,
    faq,
    is_public: config.is_public,
    subscribe: config.meta.subscribe ?? undefined,
    privacy_policy: config.meta.privacy_policy ?? undefined,
  };
}

function useTrustCenterStandardTermDocumentIds(): DocumentId[] {
  const account = useClientAccount();
  const config = useQueryData(q.external.trustCenterConfig(account));

  return useMemo(
    () => config.meta.standard_terms?.related_documents?.flatMap(d => (d.document_id ? [d.document_id] : [])) ?? [],
    [config.meta.standard_terms],
  );
}

export function useTrustCenterDocuments(): DocumentExternal[] {
  const clientAccount = useClientAccount();
  const allDocuments = useQueryData(q.external.trustCenterDocuments(clientAccount));
  const standardTermIds = useTrustCenterStandardTermDocumentIds();

  return useMemo(() => {
    const standardTermIdSet = new Set(standardTermIds);
    return allDocuments
      .filter(
        // Certifications and standard terms of business are shown on their own pages
        d => d.category !== "Certification" && !standardTermIdSet.has(d.document_id),
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allDocuments, standardTermIds]);
}

export function useTrustCenterStandardTerms(): DocumentExternal[] {
  const clientAccount = useClientAccount();
  const allDocuments = useQueryData(q.external.trustCenterDocuments(clientAccount));
  const standardTermIds = useTrustCenterStandardTermDocumentIds();

  return useMemo(() => {
    const byId = new Map(allDocuments.map(d => [d.document_id, d]));
    // Preserve the admin-configured order; drop any that aren't trust-center-visible.
    return standardTermIds.flatMap(id => {
      const doc = byId.get(id);
      return doc ? [doc] : [];
    });
  }, [allDocuments, standardTermIds]);
}

export function useTrustCenterCertifications(): DocumentExternal[] {
  const clientAccount = useClientAccount();
  const allDocuments = useQueryData(q.external.trustCenterDocuments(clientAccount));

  const certifications = allDocuments
    .filter(d => d.category === "Certification")
    .sort((a, b) => a.name.localeCompare(b.name));

  return certifications;
}

export type TrustCenterState = {
  config: ConfigT;
  documents: DocumentExternal[];
  certifications: DocumentExternal[];
  standardTerms: DocumentExternal[];
};

export function useTrustCenterState(): TrustCenterState {
  const config = useTrustCenterConfig();
  const documents = useTrustCenterDocuments();
  const certifications = useTrustCenterCertifications();
  const standardTerms = useTrustCenterStandardTerms();

  return {config, documents, certifications, standardTerms};
}
